/**
 * /de/produktmanagement — PM overview concept page (DE). Targets produkt
 * management (20) / was ist produktmanagement. Hub page linking the DE cluster.
 */
import { MarketingShell } from './MarketingShell';

export function DeProduktmanagement() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/produktmanagement"
      hreflangLocales={['de']}
      documentTitle="Produktmanagement: Aufgaben, Rolle & Tools (Überblick) · Draftlize"
      documentDescription="Was Produktmanagement umfasst, was ein Product Manager täglich tut, und welche Artefakte (PRD, Roadmap, Decision Log) die Arbeit zusammenhalten."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Leitfaden · Produktmanagement</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Produktmanagement:<br />
            <em>Rolle, Aufgaben und Artefakte.</em>
          </h1>
          <p className="lede">
            Produktmanagement heißt, das <em>richtige</em> Produkt zu bauen: herausfinden, was Nutzer brauchen, entscheiden, was gebaut wird, und das Team ausrichten. Kein Projektmanagement (das sorgt fürs pünktliche Bauen) — sondern die Frage nach dem Was und Warum. Zusammengehalten wird die Arbeit von wenigen Artefakten: PRD, Roadmap, Discovery, Decision Log. Hier der Überblick — und wie diese Artefakte aktuell bleiben.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Die Kern-Artefakte des Produktmanagements.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong><a href="/de/produktrecherche" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Produktrecherche / Discovery</a></strong> — herausfinden, was wirklich gebraucht wird.</li>
            <li><strong><a href="/de/produkt-roadmap" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Roadmap</a></strong> — Richtung und Reihenfolge, auf Outcome-Ebene.</li>
            <li><strong><a href="/de/prd-anforderungsdokument" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PRD</a></strong> — was gebaut wird und warum.</li>
            <li><strong><a href="/de/entscheidungsprotokoll" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Decision Log</a></strong> — das „Warum“ hinter jeder Entscheidung.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Der Product Manager verbindet Nutzerbedarf, Business und Technik – und hält diese Artefakte konsistent. Genau da liegt das Problem: als getrennte Dokumente driften sie auseinander.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Halte alle Artefakte an einem Ort <em>lebendig.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Discovery, Roadmap, PRD und Decision Log erzählen dieselbe Geschichte – aber in getrennten Tools driften sie. Eine Entscheidung ändert sich, und drei Dokumente stimmen nicht mehr überein. In Draftlize sind sie verknüpfte Karten.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Ein verknüpfter Graph statt vier Dokumente</h3>
            <p>Fund → Entscheidung → Anforderung → Roadmap-Eintrag: alles verlinkt, per ID adressierbar, statt in vier Tools verstreut.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Ändert sich eine Entscheidung, flaggt alles Abhängige</h3>
            <p>Roadmap und PRD werden automatisch „stale“, wenn die Entscheidung darunter kippt. Kein stilles Auseinanderdriften.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Dein KI-Agent liest den Kontext</h3>
            <p>Claude Code oder Cursor lesen die Produkt-Artefakte via MCP – aktuell oder markiert, nicht als Dokumente mit unbekanntem Stand.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Produktmanagement scheitert selten am Denken. Es scheitert daran, dass die Artefakte in getrennten Tools auseinanderdriften.
        </blockquote>
        <cite>Halte PRD, Roadmap und Entscheidungen an einem Ort lebendig. Das ist Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Häufige Fragen.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Product Manager vs. Product Owner?', a: 'Der Product Manager verantwortet das „Was und Warum“ übergreifend; der Product Owner (Scrum) priorisiert das Backlog im Team. Die Rollen überschneiden sich je nach Organisation.' },
            { q: 'Braucht man technisches Wissen?', a: 'Genug, um mit Engineering fundiert zu entscheiden – kein Entwickler-Level. Wichtiger sind Nutzerverständnis, Priorisierung und klare Entscheidungen.' },
            { q: 'Welche Tools?', a: 'Discovery-, Roadmap- und Doc-Tools – oder ein Substrat, das die Entscheidungen dahinter verknüpft und aktuell hält.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Alle Produkt-Artefakte lebendig <em>– gratis mit $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Verbinde Discovery, Roadmap, PRD und Decision Log zu einem Graphen. Ändert sich eine Entscheidung, ziehen alle betroffenen Artefakte nach.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
